import { useEffect } from 'react';

// Site-wide support for CMS tooltip markers. Any element with a `data-tooltip`
// attribute in editor-authored content shows the attribute text in a bubble on
// hover, keyboard focus or tap. It works wherever editor HTML already renders as
// HTML: the raw HTML fields (WebinarHtml, HtmlBlock, RichText) and the markdown
// fields rendered with rehype-raw. Markdown fields without rehype-raw print tags
// literally, so the marker is not available there by design.
//
// Editors write e.g. `<sup data-tooltip="Vysvětlivka">*</sup>` (`<sup>` for a small
// raised marker, `<span>` for one at text size). In markdown fields a paragraph
// with two markers needs the asterisks escaped as `\*`, otherwise markdown pairs
// them up as emphasis.
//
// The bubble itself is pure CSS (`[data-tooltip]` in globals.css). This component,
// mounted once in _app.js, adds what CSS cannot: it makes markers focusable and
// accessible, and shifts the bubble sideways when centring it on the marker would
// push it off-screen.

const SELECTOR = '[data-tooltip]';
const VIEWPORT_GUTTER = 16;

const prepareMarker = (marker) => {
  // Focusable so keyboard and touch (tap) users can open the bubble. The tooltip
  // text doubles as the accessible name because CSS-generated content is not
  // reliably exposed to screen readers.
  if (!marker.hasAttribute('tabindex')) marker.setAttribute('tabindex', '0');
  if (!marker.hasAttribute('role')) marker.setAttribute('role', 'note');
  if (!marker.hasAttribute('aria-label')) marker.setAttribute('aria-label', marker.dataset.tooltip);
};

const prepareWithin = (node) => {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  if (node.matches(SELECTOR)) prepareMarker(node);
  node.querySelectorAll(SELECTOR).forEach(prepareMarker);
};

// The bubble (`[data-tooltip]::after`) is centred on the marker. Near a screen
// edge that would push it off-screen, so shift it just enough to stay inside the
// viewport. The arrow is positioned separately and stays on the marker.
const clampTooltip = (marker) => {
  const bubbleWidth = parseFloat(getComputedStyle(marker, '::after').width) || 0;
  const rect = marker.getBoundingClientRect();
  const center = rect.left + rect.width / 2;
  const min = VIEWPORT_GUTTER + bubbleWidth / 2;
  const max = document.documentElement.clientWidth - VIEWPORT_GUTTER - bubbleWidth / 2;
  const shift = Math.min(Math.max(center, min), max) - center;
  marker.style.setProperty('--tooltip-shift', `${Math.round(shift)}px`);
};

const CmsTooltips = () => {
  useEffect(() => {
    prepareWithin(document.body);
    // Content blocks are loaded with next/dynamic and pages change client-side,
    // so watch for markers arriving later instead of scanning once.
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(prepareWithin));
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const onShow = (event) => {
      const marker = event.target.closest?.(SELECTOR);
      if (marker) clampTooltip(marker);
    };
    document.addEventListener('mouseover', onShow);
    document.addEventListener('focusin', onShow);

    return () => {
      observer.disconnect();
      document.removeEventListener('mouseover', onShow);
      document.removeEventListener('focusin', onShow);
    };
  }, []);

  return null;
};

export default CmsTooltips;
